/**
 * Carry-over Rule
 *
 * The one decision of HOW a stored design state is placed on a target print
 * area. Every consumer -- `carryOverPlacement`, the product filter, the
 * editor's "irreversible switch" flag, the save path, the print files, the
 * agent tools, the campaign import -- asks here and never decides on tiers,
 * percents or classes by itself (learning "card != editor").
 *
 * Precedence:
 *   1   the row describes this very area (same product), or is a custom
 *       placement                                   -> unchanged
 *   R   target area is RUNDUM, source tier known    -> front half of the strip
 *       (a RUNDUM target never takes a percent box, whatever the source)
 *   2   target carries a carry-over percent         -> fit into X % box, anchor
 *   3a  OBJECT source onto TEXTILE target           -> 80 % box, target anchor
 *   3b  TEXTILE source onto OBJECT target           -> master mm, capped, anchor
 *   4   KLEIN target from a larger known source     -> today's KLEIN rule
 *   5   everything else                             -> relative rule
 *
 * The identity of rule 1 is the product id: the snapshot of a row names the
 * product its state was measured on (`sourceProductId`), the target names
 * the product being rendered. `isMaster` is the fallback for rows that
 * predate the field, and the criterion the save path pins the master by.
 * It is required so no reader can forget the master and silently diverge.
 */

import {
  DEFAULT_TOP_OFFSET_MM,
  OBJECT_TO_TEXTILE_PERCENT,
  defaultAnchorForClass,
  isProductClass,
  isValidCarryOverPercent,
  type CarryOverAnchor,
} from './carryOverDefaults.js';
import { maximisesOnTier, type PlacementArea } from '../placement/sizeTier.js';

/**
 * What the rule reads of an area: identity, class, tier and settings. A
 * `PlacementArea` satisfies it; the product filter passes catalogue rows
 * without a pixel size.
 */
export type CarryOverRuleArea = Pick<
  PlacementArea,
  | 'productId'
  | 'productClass'
  | 'sizeTier'
  | 'sizeTierOverride'
  | 'carryOverPercent'
  | 'carryOverAnchor'
  | 'carryOverTopOffsetMm'
>;

export type CarryOverRuleName =
  | 'master'
  | 'custom'
  | 'rundum'
  | 'percent'
  | 'object-to-textile'
  | 'textile-to-object'
  | 'klein'
  | 'relative';

export interface CarryOverRuleOptions {
  /**
   * Whether the state belongs to the design's master product. Decides rule 1
   * only when source or target carry no product id (legacy rows, in-memory
   * contexts without an id); with ids on both sides the ids decide.
   */
  isMaster: boolean;
  /** A persisted per-product placement bypasses every rule. */
  isCustom?: boolean;
}

/** The box a percent rule fits the motif into. */
export interface CarryOverBox {
  /** Share of the target area the box takes on each axis (1-100). */
  percent: number;
  anchor: CarryOverAnchor;
  /** Distance of the box's top edge from the area's top edge (anchor TOP). */
  topOffsetMm: number;
  /** Never wider in mm than on the master (rule 3b). */
  capAtMasterMm: boolean;
}

export type CarryOverDecision =
  | { rule: 'master' | 'custom' | 'rundum' | 'klein' | 'relative' }
  | {
      rule: 'percent' | 'object-to-textile' | 'textile-to-object';
      box: CarryOverBox;
    };

/** Rules whose placement comes from the TARGET, not from the master's state. */
const TARGET_DRIVEN_RULES: readonly CarryOverRuleName[] = [
  'rundum',
  'percent',
  'object-to-textile',
  'textile-to-object',
  'klein',
];

/**
 * Whether a rule places from the target area instead of carrying the
 * master's relative state: such a result cannot be reversed by the relative
 * rule, so an edit on it must become a custom placement (today's
 * `derivedKlein`), and the step-2 width check does not apply.
 */
export function isTargetDrivenRule(rule: CarryOverRuleName): boolean {
  return TARGET_DRIVEN_RULES.includes(rule);
}

/**
 * Whether a rule may enlarge the motif beyond its master size. Rule 3b
 * keeps the master's millimetres, so it does not "maximise" (agent tools).
 */
export function ruleMayEnlarge(rule: CarryOverRuleName): boolean {
  return (
    rule === 'rundum' ||
    rule === 'percent' ||
    rule === 'object-to-textile' ||
    rule === 'klein'
  );
}

function hasId(
  area: CarryOverRuleArea
): area is CarryOverRuleArea & { productId: string } {
  return typeof area.productId === 'string' && area.productId.length > 0;
}

/**
 * The percent box a TARGET area asks for, or `null`. A KLEIN override still
 * in the catalogue (the retire migration has not run yet) reads as percent
 * 100, centered -- exactly what the override meant.
 */
export function targetPercentBox(
  target: CarryOverRuleArea
): CarryOverBox | null {
  if (isValidCarryOverPercent(target.carryOverPercent)) {
    return {
      percent: target.carryOverPercent,
      anchor:
        target.carryOverAnchor ?? defaultAnchorForClass(target.productClass),
      topOffsetMm: target.carryOverTopOffsetMm ?? DEFAULT_TOP_OFFSET_MM,
      capAtMasterMm: false,
    };
  }
  if (target.sizeTierOverride === 'KLEIN') {
    return {
      percent: 100,
      anchor: 'CENTER',
      topOffsetMm: DEFAULT_TOP_OFFSET_MM,
      capAtMasterMm: false,
    };
  }
  return null;
}

export function carryOverRule(
  source: CarryOverRuleArea,
  target: CarryOverRuleArea,
  options: CarryOverRuleOptions
): CarryOverDecision {
  if (options.isCustom === true) return { rule: 'custom' };

  // Rule 1: the row describes this very area.
  if (hasId(source) && hasId(target)) {
    if (source.productId === target.productId) return { rule: 'master' };
  } else if (options.isMaster) {
    return { rule: 'master' };
  }

  // RUNDUM: as today, from any known and different source tier.
  if (
    target.sizeTier === 'RUNDUM' &&
    maximisesOnTier(source.sizeTier, target.sizeTier)
  ) {
    return { rule: 'rundum' };
  }

  // Rule 2: the target asks for a percent. A wrap strip never does: its
  // front-half convention is the only placement production prints, so a
  // product percent inherited onto a RUNDUM area (or a RUNDUM -> RUNDUM /
  // unknown-tier source that the rule above does not catch) is ignored.
  const percentBox =
    target.sizeTier === 'RUNDUM' ? null : targetPercentBox(target);
  if (percentBox) return { rule: 'percent', box: percentBox };

  // Rules 3a/3b: a class change between known classes.
  if (
    isProductClass(source.productClass) &&
    isProductClass(target.productClass) &&
    source.productClass !== target.productClass
  ) {
    const anchor =
      target.carryOverAnchor ?? defaultAnchorForClass(target.productClass);
    const topOffsetMm = target.carryOverTopOffsetMm ?? DEFAULT_TOP_OFFSET_MM;
    if (target.productClass === 'TEXTILE') {
      return {
        rule: 'object-to-textile',
        box: {
          percent: OBJECT_TO_TEXTILE_PERCENT,
          anchor,
          topOffsetMm,
          capAtMasterMm: false,
        },
      };
    }
    return {
      rule: 'textile-to-object',
      box: { percent: 100, anchor, topOffsetMm, capAtMasterMm: true },
    };
  }

  // Rule 4: today's KLEIN rule between same or unknown classes.
  if (
    target.sizeTier === 'KLEIN' &&
    maximisesOnTier(source.sizeTier, target.sizeTier)
  ) {
    return { rule: 'klein' };
  }

  return { rule: 'relative' };
}
